import { yupResolver } from "@hookform/resolvers/yup";
import { CourseApi, CourseScheduleDetailsApi } from "api";
import { CourseSessionOption, TopicOption, useTopicsOptions } from "api/hooks";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { PaziaButton, PaziaSelect } from "components/atoms";
import { minutesToTime } from "utils";
import { BasePlaceScheduleForm, ScheduleFormPlace } from "../BasePlaceScheduleForm";
import { BaseTrainersScheduleForm, ScheduleFormTrainers } from "../BaseTrainersScheduleForm";
import { BaseScheduleForm, ScheduleFormBase } from "./BaseScheduleForm";

interface ScheduleFormProps {
  course: CourseApi;
  /** Resolve `false` when nothing was saved (e.g. the user went back after a conflict warning): the form keeps its values. */
  onSubmit: (data: ScheduleTopicFormSchema) => Promise<boolean | void> | void;
  item?: CourseScheduleDetailsApi;
  sessionsOptions: CourseSessionOption[];
  formStyle: React.CSSProperties;
  formClassName?: string;
}

export const ScheduleTopicForm = ({
  course,
  onSubmit,
  sessionsOptions,
  formStyle,
  formClassName,
  item,
}: ScheduleFormProps): JSX.Element => {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...ScheduleFormBase.resolveDefaultValues(item?.schedule),
      ...ScheduleFormPlace.resolveDefaultValues(item?.schedule),
      ...ScheduleFormTrainers.resolveDefaultValues(item?.trainers),
      [SCHEDULE_TOPIC_FORM_NAME.TOPIC]: item?.schedule?.topicId?.toString() ?? "",
    },
  });
  const { handleSubmit, watch, setValue, reset } = methods;
  const {
    group: { id },
  } = course;

  const { loading: loadingTopics, options: topicsOptions } = useTopicsOptions(false, `courseGroups in (${id})`);

  const onSubmitHandler = async (data: ScheduleTopicFormSchema) => {
    if ((await onSubmit(data)) === false) return;
    reset({});
  };

  const onTopicChange = (option: TopicOption | null) => {
    const topicOpt = topicsOptions.find((x) => x.value == option?.value);
    const trainers =
      topicOpt?.topic?.trainers.map((x) => ({
        trainerId: x.trainerId.toString(),
        totalAmount: null,
      })) ?? [];
    const time = topicOpt?.topic?.time ?? 0;
    setValue(ScheduleFormTrainers.fields.TRAINERS, trainers);
    if (time === 0) return;
    setValue(ScheduleFormBase.fields.TIME, minutesToTime(time));
    setValue(ScheduleFormBase.fields.TO_TIME, 0);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitHandler)} className="flex grow flex-col justify-between">
        <div className={` ${formClassName}`} style={formStyle}>
          <BaseScheduleForm sessionsOptions={sessionsOptions}>
            <PaziaSelect
              required
              label="Temat"
              isLoading={loadingTopics}
              options={topicsOptions}
              name={SCHEDULE_TOPIC_FORM_NAME.TOPIC}
              onChange={onTopicChange}
              tooltip="Wyświetlane są jedynie tematy powiązane z wybraną grupą kursu."
              noOptionsMessage="Brak tematów powiązanych z grupą tego kursu — ustaw „Grupa kursu” w Tematy"
            />
          </BaseScheduleForm>
          <BasePlaceScheduleForm />
          <BaseTrainersScheduleForm />
        </div>
        <div className="mt-8 flex justify-end">
          <PaziaButton title="Dodaj zajęcia" type="success" />
        </div>
      </form>
    </FormProvider>
  );
};

const SCHEDULE_TOPIC_FORM_NAME = {
  TOPIC: "topic",
} as const;

const topicSchema = yup
  .object({
    [SCHEDULE_TOPIC_FORM_NAME.TOPIC]: yup.string().required(),
  })
  .required();

const schema = topicSchema
  .concat(ScheduleFormBase.schema)
  .concat(ScheduleFormPlace.schema)
  .concat(ScheduleFormTrainers.schema);

export type ScheduleTopicFormSchema = yup.InferType<typeof schema>;
